/**
 * DragDropOverlay 组件
 * 
 * 在用户拖拽文件到聊天界面时显示的全屏覆盖层
 * 提供视觉反馈，指示用户可以在此处释放文件进行上传
 * 
 * 设计特点：
 * - 全屏覆盖整个聊天区域（消息历史 + 输入区域）
 * - iOS风格的半透明蓝色背景
 * - 虚线边框和居中的提示文本
 * - 平滑的淡入淡出动画效果
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface DragDropOverlayProps {
  /** 是否显示覆盖层 */
  isVisible: boolean;
  /** 自定义样式类名 */
  className?: string;
}

export const DragDropOverlay: React.FC<DragDropOverlayProps> = ({
  isVisible,
  className = ''
}) => {
  const { t } = useTranslation(['v3/chat']);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`
            absolute inset-0 z-[70]
            bg-ios-blue/10 dark:bg-ios-dark-blue/20
            border-2 border-dashed border-ios-blue dark:border-ios-dark-blue
            rounded-ios-lg
            flex items-center justify-center
            ${className}
          `}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-center">
            {/* 文件图标 */}
            <div className="mb-4">
              <svg 
                className="w-16 h-16 mx-auto text-ios-blue dark:text-ios-dark-blue" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                />
              </svg>
            </div>
            
            {/* 提示文本 */}
            <div className="text-ios-blue dark:text-ios-dark-blue font-medium text-lg">
              {t('dragDropHint', '拖拽文件到这里上传')}
            </div>
            
            {/* 副标题 */}
            <div className="text-ios-gray dark:text-ios-dark-gray text-sm mt-2">
              {t('dragDropSubtitle', '支持图片、文档、音频、视频等多种格式')}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DragDropOverlay;
